import { Box, Typography } from '@mui/material';
import React, { useCallback } from 'react';

import { EmptyTable } from '@/shared/components/ui/data-table/components/empty-table/empty-table';
import { useDataTable } from '@/shared/components/ui/data-table/context/data-table-context';
import { TUseEmptyStateRendererParams } from '@/shared/components/ui/data-table/hooks/use-empty-state-renderer/use-empty-state-renderer.types';
import { useHasFilters } from '@/shared/components/ui/data-table/hooks/use-filter-params/use-filter-params';
import { EmptyResults } from '@/shared/components/ui/form/components/searchable-select/components/empty-results/empty-results';
import { useFilterScope } from '@/shared/features/filter-preferences/context/filter-scope-context';

export const useEmptyStateRenderer = ({
  customEmptyMessage,
  emptyMessageVariant = 'p7' as const,
  emptyMessageColor,
}: TUseEmptyStateRendererParams) => {
  const { data } = useDataTable();
  const { hasSearchParams } = useHasFilters();
  const { isReady } = useFilterScope();
  const { isLoading, isFetching } = data;
  // A gated (skipped) query reports isLoading false, so treat "scope not ready"
  // as loading — otherwise the empty-state renders before the first rows.
  const _isLoading = !isReady || isLoading || isFetching;

  return useCallback((): React.ReactNode => {
    if (_isLoading) return <Box height="10vh" />;

    if (customEmptyMessage) {
      if (typeof customEmptyMessage !== 'string') return customEmptyMessage;
      return (
        <Typography variant={emptyMessageVariant} color={emptyMessageColor}>
          {customEmptyMessage}
        </Typography>
      );
    }

    if (hasSearchParams) return <EmptyResults />;
    return <EmptyTable />;
  }, [
    _isLoading,
    customEmptyMessage,
    hasSearchParams,
    emptyMessageVariant,
    emptyMessageColor,
  ]);
};
